import { useState, type FormEvent } from 'react';

import type { AuditEvent, Tenant } from '../domain-types';
import { Modal } from '../components/modal';
import { Button } from '../components/ui-controls';

type TenantSupportSnapshot = {
  readOnly: true;
  accessedAt: string;
  tenant: Pick<
    Tenant,
    | 'id'
    | 'name'
    | 'status'
    | 'onboardingStatus'
    | 'offboardingStatus'
    | 'mfaRequired'
    | 'providerVerificationStatus'
    | 'subscriptionAccessUntil'
  > & { createdAt?: string; updatedAt?: string };
  membershipCounts: Record<string, number>;
  roleCounts: Record<string, number>;
  totalMemberships: number;
  commercialState: 'trial' | 'standard';
};

export function AuditHistoryPanel({
  events,
  onExport,
  visible,
}: {
  events: AuditEvent[];
  onExport: () => void;
  visible: boolean;
}) {
  if (events.length === 0 && !visible) return null;
  return (
    <section className="audit-panel" aria-labelledby="audit-title">
      <div className="workspace-heading">
        <div>
          <p className="eyebrow">Assurance</p>
          <h2 id="audit-title">Recent audit history</h2>
        </div>
        <button type="button" onClick={onExport}>
          Export JSON
        </button>
      </div>
      <div className="audit-list">
        {events.slice(0, 20).map((event) => (
          <article key={event.id}>
            <div>
              <strong>{event.action}</strong>
              <span>{event.outcome}</span>
            </div>
            <small>
              {new Date(event.occurredAt).toLocaleString()} · {event.targetType} · {event.targetId}
            </small>
          </article>
        ))}
        {events.length === 0 ? <p>No audit events are available yet.</p> : null}
      </div>
    </section>
  );
}

export function PlatformStatusPanel({ apiUrl }: { apiUrl: string }) {
  return (
    <section id="platform-health" className="status-strip" aria-label="Platform health">
      <div>
        <span className="status-dot" />
        <strong>Development skeleton</strong>
        <small>Synthetic data only</small>
      </div>
      <p>
        API: <code>{apiUrl}</code>
      </p>
    </section>
  );
}

export function PlatformAdminPanel({
  accessToken,
  apiUrl,
  visible,
  tenants,
  newTenantName,
  newTenantSlug,
  newTenantAdministratorEmail,
  busy,
  giftDurationDays,
  giftReason,
  roleMigrationBusy,
  roleMigrationMessage,
  onNewTenantNameChange,
  onNewTenantSlugChange,
  onNewTenantAdministratorEmailChange,
  onGiftDurationDaysChange,
  onGiftReasonChange,
  onCreateTenant,
  onUpdateTenantLifecycle,
  onGiftSubscription,
  onMigrateLegacyRoles,
}: {
  accessToken: string;
  apiUrl: string;
  visible: boolean;
  tenants: Tenant[];
  newTenantName: string;
  newTenantSlug: string;
  newTenantAdministratorEmail: string;
  busy: boolean;
  giftDurationDays: number;
  giftReason: string;
  roleMigrationBusy: boolean;
  roleMigrationMessage: string;
  onNewTenantNameChange: (value: string) => void;
  onNewTenantSlugChange: (value: string) => void;
  onNewTenantAdministratorEmailChange: (value: string) => void;
  onGiftDurationDaysChange: (value: number) => void;
  onGiftReasonChange: (value: string) => void;
  onCreateTenant: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  onUpdateTenantLifecycle: (tenantId: string, update: Record<string, unknown>) => Promise<void>;
  onGiftSubscription: (tenantId: string) => Promise<void>;
  onMigrateLegacyRoles: () => Promise<void>;
}) {
  const [supportSnapshot, setSupportSnapshot] = useState<TenantSupportSnapshot>();
  const [supportSnapshotError, setSupportSnapshotError] = useState('');
  const [supportSnapshotBusyId, setSupportSnapshotBusyId] = useState('');

  const openSupportSnapshot = async (tenantId: string) => {
    setSupportSnapshotBusyId(tenantId);
    setSupportSnapshotError('');
    try {
      const response = await fetch(
        `${apiUrl}/v1/platform/tenants/${encodeURIComponent(tenantId)}/support-snapshot`,
        { headers: { authorization: `Bearer ${accessToken}` } },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setSupportSnapshot((await response.json()) as TenantSupportSnapshot);
    } catch {
      setSupportSnapshotError('The read-only support view could not be opened.');
    } finally {
      setSupportSnapshotBusyId('');
    }
  };

  if (!visible) return null;

  return (
    <section className="platform-admin-panel" aria-labelledby="platform-admin-title">
      <div className="workspace-heading">
        <div>
          <p className="eyebrow">Platform administration</p>
          <h2 id="platform-admin-title">Organisation onboarding and lifecycle</h2>
        </div>
        <span>{tenants.length} organisations</span>
      </div>
      <form className="tenant-create-form" onSubmit={(event) => void onCreateTenant(event)}>
        <label>
          Organisation name
          <input
            required
            maxLength={160}
            value={newTenantName}
            onChange={(event) => onNewTenantNameChange(event.target.value)}
          />
        </label>
        <label>
          URL-safe slug
          <input
            required
            pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
            value={newTenantSlug}
            onChange={(event) => onNewTenantSlugChange(event.target.value)}
          />
        </label>
        <label>
          Primary administrator email
          <input
            required
            type="email"
            value={newTenantAdministratorEmail}
            onChange={(event) => onNewTenantAdministratorEmailChange(event.target.value)}
          />
        </label>
        <button disabled={busy} type="submit">
          Create and invite
        </button>
      </form>
      <div className="gift-controls">
        <label>
          Complimentary access (days)
          <input
            type="number"
            min={1}
            max={1095}
            value={giftDurationDays}
            onChange={(event) => onGiftDurationDaysChange(Number(event.target.value))}
          />
        </label>
        <label>
          Gift reason
          <input
            minLength={10}
            maxLength={2000}
            value={giftReason}
            onChange={(event) => onGiftReasonChange(event.target.value)}
          />
        </label>
        <p>
          Gifts extend the organisation’s existing access period and never create a Stripe payment.
        </p>
      </div>
      <div className="legacy-role-migration">
        <div>
          <strong>Legacy role migration</strong>
          <p>
            Convert historic Viewer, Auditor and Quality Manager memberships to the current role
            identifiers. Auditors become Standard Users to avoid a silent privilege increase.
          </p>
          {roleMigrationMessage ? <small>{roleMigrationMessage}</small> : null}
        </div>
        <button
          disabled={roleMigrationBusy}
          type="button"
          onClick={() => void onMigrateLegacyRoles()}
        >
          {roleMigrationBusy ? 'Migrating…' : 'Migrate legacy roles'}
        </button>
      </div>
      <div className="tenant-lifecycle-list">
        {tenants.map((tenant) => (
          <article key={tenant.id}>
            <div>
              <strong>{tenant.name}</strong>
              <small>
                {tenant.slug} · {tenant.status} · onboarding{' '}
                {tenant.onboardingStatus ?? 'not started'}
              </small>
              <small>Offboarding: {tenant.offboardingStatus ?? 'none'}</small>
              <small>
                Subscription access:{' '}
                {tenant.subscriptionAccessUntil
                  ? new Date(tenant.subscriptionAccessUntil).toLocaleString()
                  : 'not granted'}
              </small>
            </div>
            <div className="tenant-lifecycle-actions">
              {tenant.status === 'onboarding' ? (
                <button
                  disabled={busy}
                  type="button"
                  onClick={() =>
                    void onUpdateTenantLifecycle(tenant.id, {
                      status: 'active',
                      onboardingStatus: 'complete',
                      onboardingChecklist: {
                        organisationDetailsConfirmed: true,
                        primaryAdministratorInvited: true,
                        dataRegionConfirmed: true,
                      },
                    })
                  }
                >
                  Complete onboarding
                </button>
              ) : tenant.status === 'active' ? (
                <button
                  disabled={busy}
                  type="button"
                  onClick={() => void onUpdateTenantLifecycle(tenant.id, { status: 'suspended' })}
                >
                  Suspend access
                </button>
              ) : tenant.status === 'suspended' ? (
                <button
                  disabled={busy}
                  type="button"
                  onClick={() => void onUpdateTenantLifecycle(tenant.id, { status: 'active' })}
                >
                  Reactivate
                </button>
              ) : null}
              {tenant.offboardingStatus === 'requested' ? (
                <button
                  disabled={busy}
                  type="button"
                  onClick={() =>
                    void onUpdateTenantLifecycle(tenant.id, { offboardingStatus: 'export_ready' })
                  }
                >
                  Mark export ready
                </button>
              ) : null}
              {tenant.offboardingStatus === 'export_ready' && tenant.status !== 'archived' ? (
                <button
                  disabled={busy}
                  type="button"
                  onClick={() => void onUpdateTenantLifecycle(tenant.id, { status: 'archived' })}
                >
                  Archive organisation
                </button>
              ) : null}
              <Button
                disabled={supportSnapshotBusyId === tenant.id}
                type="button"
                onClick={() => void openSupportSnapshot(tenant.id)}
              >
                {supportSnapshotBusyId === tenant.id ? 'Opening…' : 'Read-only support view'}
              </Button>
              <button
                disabled={
                  busy ||
                  tenant.status === 'archived' ||
                  giftDurationDays < 1 ||
                  giftDurationDays > 1095 ||
                  giftReason.trim().length < 10
                }
                type="button"
                onClick={() => void onGiftSubscription(tenant.id)}
              >
                Gift {giftDurationDays} days
              </button>
            </div>
          </article>
        ))}
      </div>
      {supportSnapshotError ? (
        <p className="auth-error" role="alert">
          {supportSnapshotError}
        </p>
      ) : null}
      {supportSnapshot ? (
        <Modal
          label={`Read-only support view for ${supportSnapshot.tenant.name}`}
          onClose={() => setSupportSnapshot(undefined)}
        >
          <div className="app-modal-header">
            <p className="eyebrow">Super Administrator</p>
            <h2>{supportSnapshot.tenant.name}</h2>
            <p className="read-only-support-banner">
              Read-only support view · Access is audited · No organisation data can be changed here
            </p>
          </div>
          <div className="app-modal-content support-snapshot-content">
            <dl>
              <div>
                <dt>Lifecycle</dt>
                <dd>{supportSnapshot.tenant.status}</dd>
              </div>
              <div>
                <dt>Commercial state</dt>
                <dd>{supportSnapshot.commercialState}</dd>
              </div>
              <div>
                <dt>Members</dt>
                <dd>{supportSnapshot.totalMemberships}</dd>
              </div>
              <div>
                <dt>MFA required</dt>
                <dd>{supportSnapshot.tenant.mfaRequired ? 'Yes' : 'No'}</dd>
              </div>
              <div>
                <dt>Onboarding</dt>
                <dd>{supportSnapshot.tenant.onboardingStatus ?? 'Not started'}</dd>
              </div>
              <div>
                <dt>Offboarding</dt>
                <dd>{supportSnapshot.tenant.offboardingStatus ?? 'None'}</dd>
              </div>
            </dl>
            <section>
              <h3>Account status</h3>
              <p>
                {Object.entries(supportSnapshot.membershipCounts)
                  .map(([label, count]) => `${label}: ${count}`)
                  .join(' · ') || 'No memberships'}
              </p>
            </section>
            <section>
              <h3>Roles</h3>
              <p>
                {Object.entries(supportSnapshot.roleCounts)
                  .map(([label, count]) => `${label}: ${count}`)
                  .join(' · ') || 'No roles'}
              </p>
            </section>
            <small>Snapshot accessed {new Date(supportSnapshot.accessedAt).toLocaleString()}</small>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
